import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CheckCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { PaymentMethod, PAYMENT_METHOD_TEXT } from '~/types/Payment';
import { formatCurrency } from '~/utils/currency';
import { Button, Space } from 'antd';
import { DrawerContext } from '~/providers/DrawerProvider';
import './style.css';
import { usePrintOrder } from '~/App/hooks';
import { useCheckPermission } from '~/store/permission';
import { Order } from '@restaurant/api';

const FinishText = ({ onTimerEnd }: { onTimerEnd: () => void }) => {
  const [value, setValue] = useState(15);

  useEffect(() => {
    const timer = setInterval(() => {
      setValue((val) => {
        const newVal = val - 1;
        if (newVal === 0) {
          onTimerEnd();
        }
        return newVal;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return <p>{`Selesai (${value})`}</p>;
};

const PaymentDonePanel = ({
  order,
  totalPrice,
  roundingAmount,
  payments,
  onSubmit,
}: {
  order: Order;
  totalPrice: number;
  roundingAmount: number;
  payments: { method: PaymentMethod; amount: number }[];
  onSubmit: () => void;
}) => {
  const { printOrderDetailReceipt } = usePrintOrder();
  const checkPermission = useCheckPermission();

  const canPrint = checkPermission('orderReceiptPrintCashier:r');

  const onPrint = async () => {
    if (!canPrint) return;
    await printOrderDetailReceipt(order.orderId);
  };

  const onDone = () => {
    onSubmit();
  };

  const totalPayment = payments.reduce((acc, cur) => {
    acc += cur.amount;
    return acc;
  }, 0);
  const changeValue = totalPayment - (totalPrice - roundingAmount);

  const rows = [
    ...(roundingAmount > 0
      ? [
          { text: 'Subtotal Tagihan', value: totalPrice },
          { text: 'Rounding', value: 0 - roundingAmount },
        ]
      : []),
    {
      text: 'Total tagihan',
      value: totalPrice - roundingAmount,
      size: 'large',
    },
    ...(payments.length > 1
      ? [
          ...payments.map(({ method, amount }) => ({
            text: PAYMENT_METHOD_TEXT[method],
            value: amount,
          })),
          { text: 'Total Bayar', value: totalPayment, size: 'large' },
        ]
      : [
          {
            text: `Pembayaran (${PAYMENT_METHOD_TEXT[payments[0].method]})`,
            value: totalPayment,
            size: 'large',
          },
        ]),
    { text: 'Kembalian', value: changeValue, size: 'large' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div
          className="d-flex justify-content-center"
          style={{ marginBottom: 16 }}
        >
          <CheckCircleOutlined style={{ color: '#3fa63f', fontSize: 70 }} />
        </div>
        <div className="flex flex-col w-full gap-2">
          {rows.map(({ text, value, size }, index) => (
            <div
              key={`${text}-${value}-${index}`}
              className={[
                'flex justify-between items-center font-medium',
                size === 'large' ? 'text-xl' : '',
              ].join(' ')}
            >
              <p className="mb-0">{text}</p>
              <p className="mb-0">{formatCurrency(value)}</p>
            </div>
          ))}
        </div>
      </div>
      <Space style={{ width: '100%' }} direction="vertical">
        {canPrint ? (
          <Button
            icon={<PrinterOutlined />}
            size="large"
            onClick={onPrint}
            block
          >
            Print struk
          </Button>
        ) : null}
        <Button type="primary" size="large" onClick={onDone} block>
          <FinishText onTimerEnd={onDone} />
        </Button>
      </Space>
    </div>
  );
};

const useOpenPaymentDonePanel = () => {
  const { openDrawer } = useContext(DrawerContext);
  return useCallback(
    ({
      order,
      totalPrice,
      roundingAmount,
      payments,
      onSubmit,
    }: {
      order: Order;
      totalPrice: number;
      roundingAmount: number;
      payments: { method: PaymentMethod; amount: number }[];
      onSubmit: () => void;
    }) => {
      openDrawer(
        ({ closeDrawer }) => {
          const handleSubmit = () => {
            closeDrawer();
            onSubmit();
          };
          return (
            <PaymentDonePanel
              order={order}
              totalPrice={totalPrice}
              roundingAmount={roundingAmount}
              payments={payments}
              onSubmit={handleSubmit}
            />
          );
        },
        {
          height: '80%',
          placement: 'bottom',
          closable: false,
          maskClosable: false,
        }
      );
    },
    []
  );
};

export default useOpenPaymentDonePanel;
